import { server } from "../../server";
import { useEffect, useState } from "react";
import axios from "axios";
import { classList, sectionList } from "../../utils";

function ExaminationMarks() {
  const [studentMarks, setStudentMarks] = useState([]);
  const [examHeads, setExamHeads] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [studentClass, setStudentClass] = useState("II");
  const [section, setSection] = useState("A");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const marksResponse = await axios.get(
          `${server}/GetStudentMarksThroughFilter?session=2024-2025&class=${studentClass}&section=${section}&examType=half yearly`,
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );
        const studentData = marksResponse.data;
        if (Array.isArray(studentData)) {
          setStudentMarks(studentData);
        }

        const headsResponse = await axios.get(`${server}/GetAllResultHeads`, {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        });
        const headsData = JSON.parse(headsResponse.data.body);

        const classHeads = headsData.find(
          (item) =>
            item.class.includes(studentClass) && item.session === "2024-2025",
        );

        if (classHeads) {
          const halfYearly = classHeads.examHeads.find(
            (e) => e.examType.toLowerCase() === "halfyearly",
          );
          if (halfYearly) {
            const headsMap = {};
            halfYearly.heads.forEach((h) => {
              headsMap[h.id] = h.label;
            });
            setExamHeads(headsMap);
          }
        }
      } catch (err) {
        setError("Failed to load data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [studentClass, section]);

  // Filter
  const filteredRecords = studentMarks.filter((studentMarks) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(studentMarks || {}).some(
      (value) =>
        typeof value === "string" && value.toLowerCase().includes(searchLower),
    );
  });

  // ✅ Reset pagination when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ✅ Apply pagination AFTER filtering
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="">
            <div class="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table class="table mb-0 text-nowrap table-centered">
                      <thead class="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((student, index) => (
                          <tr key={student._id}>
                            <td>{indexOfFirstRecord + index + 1}.</td>
                            <td className="lh-sm py-5">
                              <span className="text-dark">Admission No</span>{" "}
                              &mdash; {student.admission_no} <br />
                              <span className="text-dark">
                                Class
                              </span> &mdash; {student.class} <br />
                              <span className="text-dark">
                                Section
                              </span> &mdash; {student.section} <br />
                              <span className="text-dark">
                                Session
                              </span> &mdash; {student.session} <br />
                              <div>
                                {student.result["half yearly"]?.map(
                                  (subject, subjIdx) => (
                                    <div key={subjIdx} className="mb-1">
                                      <br />
                                      <span className="text-capitalize text-danger">
                                        {subject.subject}
                                        <br />
                                      </span>
                                      {Object.entries(subject.marks).map(
                                        ([examId, mark]) => {
                                          const label =
                                            examHeads[examId] || examId;
                                          return (
                                            <>
                                              <span key={examId}>
                                                {label} — {mark}
                                                <br />
                                              </span>
                                            </>
                                          );
                                        },
                                      )}
                                    </div>
                                  ),
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 width-fit-content">
                                <span class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm">
                                  Add Marks
                                </span>
                                <span className="badge bg-danger-soft cursor-pointer px-4">
                                  Update Marks
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div class="">
                    <div className="mb-3">
                      <div className="bg-white rounded-pill">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol class="breadcrumb pe-3">
                            <li class="breadcrumb-item">
                              <a href="#">Examinations</a>
                            </li>
                            <li
                              class="breadcrumb-item active"
                              aria-current="page"
                            >
                              Examination Marks
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4 mb-4">
                      <div class="bg-danger-soft rounded-4 p-3 d-flex align-items-center">
                        <div class="avatar-group">
                          {[1, 2, 3, 4, 5].map((num) => (
                            <span className="avatar avatar-md" key={num}>
                              <img
                                alt={`avatar-${num}`}
                                src={`/assets/images/students/${num}.jpg`}
                                className="rounded-circle"
                              />
                            </span>
                          ))}
                        </div>
                        <h3 className="fw-bold m-0 text-center fs-4 ms-3">
                          {studentMarks.length.toLocaleString("en-IN")}
                        </h3>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="d-flex align-items-center">
                          {loading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" />
                          )}

                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            Examination Marks
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              3.2 MB Size
                            </span>
                          </p>
                        </div>
                        <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                          Export
                        </span>
                      </div>

                      <div class="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#specifications"
                            role="button"
                            aria-expanded="false"
                            aria-controls="specifications"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div
                            className="collapse"
                            id="specifications"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3">
                              <div class="rounded-4 d-flex flex-wrap">
                                <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                  Class / Section Wise
                                </span>
                                <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                  Class Wise
                                </span>
                                <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                  All Student's Report
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>

                      <div class="mt-4">
                        <div className="position-relative mt-2">
                          <span
                            className="circle-slider-button bg-dark rounded-circle position-absolute start-0 top-50 translate-middle-y z-1 cursor-pointer"
                            onClick={() =>
                              document
                                .getElementById("classScroll")
                                .scrollBy({ left: -120, behavior: "smooth" })
                            }
                          >
                            <i className="bi bi-chevron-left text-white"></i>
                          </span>
                          <div
                            id="classScroll"
                            className="gap-1 d-flex overflow-x-auto px-6"
                            style={{
                              scrollBehavior: "smooth",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {classList.map((cls) => (
                              <span
                                key={cls}
                                className={`btn btn-tag btn-sm ${
                                  studentClass === cls
                                    ? "bg-success-subtle border border-success text-success-emphasis"
                                    : ""
                                }`}
                                onClick={() => setStudentClass(cls)}
                              >
                                {cls}
                              </span>
                            ))}
                          </div>
                          <span
                            className="circle-slider-button bg-dark rounded-circle position-absolute end-0 top-50 translate-middle-y z-1 cursor-pointer"
                            onClick={() =>
                              document
                                .getElementById("classScroll")
                                .scrollBy({ left: 120, behavior: "smooth" })
                            }
                          >
                            <i className="bi bi-chevron-right text-white"></i>
                          </span>
                        </div>

                        <div className="position-relative mt-2">
                          <span
                            className="circle-slider-button bg-dark rounded-circle position-absolute start-0 top-50 translate-middle-y z-1 cursor-pointer"
                            onClick={() =>
                              document
                                .getElementById("sectionScroll")
                                .scrollBy({ left: -120, behavior: "smooth" })
                            }
                          >
                            <i className="bi bi-chevron-left text-white"></i>
                          </span>
                          <div
                            id="sectionScroll"
                            className="gap-1 d-flex overflow-x-auto px-6"
                            style={{
                              scrollBehavior: "smooth",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {sectionList.map((sec) => (
                              <span
                                key={sec}
                                className={`btn btn-tag btn-sm ${
                                  section === sec
                                    ? "bg-success-subtle border border-success text-success-emphasis"
                                    : ""
                                }`}
                                onClick={() => setSection(sec)}
                              >
                                {sec}
                              </span>
                            ))}
                          </div>
                          <span
                            className="circle-slider-button bg-dark rounded-circle position-absolute end-0 top-50 translate-middle-y z-1 cursor-pointer"
                            onClick={() =>
                              document
                                .getElementById("sectionScroll")
                                .scrollBy({ left: 120, behavior: "smooth" })
                            }
                          >
                            <i className="bi bi-chevron-right text-white"></i>
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>

                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);

                            if (currentPage > 3) {
                              pageNumbers.push("left-ellipsis");
                            }

                            if (
                              currentPage !== 1 &&
                              currentPage !== totalPages
                            ) {
                              pageNumbers.push(currentPage);
                            }

                            if (currentPage < totalPages - 2) {
                              pageNumbers.push("right-ellipsis");
                            }

                            if (totalPages > 1) {
                              pageNumbers.push(totalPages);
                            }

                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={index}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}

                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default ExaminationMarks;
